import React, { FunctionComponent } from 'react';
import { ApolloError } from 'apollo-boost';
import { Grid, Statistic } from 'semantic-ui-react';
import { path, sum, pluck, pathOr } from 'ramda';
import { match } from 'react-router-dom';
import moment from 'moment';
import withStyles from 'react-jss';
import { FormattedNumber } from 'react-intl';

import Query from 'components/Query';
import { MerchantStatsPlaceholder } from 'components/Placeholder';
import { ClassProp } from 'helpers/types';
import { abbreviateNumber } from 'helpers/stringFunctions';
import { Transactions } from 'components/TransactionsChart/TransactionsChart.helpers';

import { Merchant } from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantDetails/MerchantDetails';

import { GET_MERCHANT_CN_STATUS } from './MerchantCNStatusPanel.queries';
import styles from './MerchantCNStatusPanel.styles';

type Styles = ClassProp<typeof styles>;

type Props = {
  classes: Styles;
  merchantId: string;
  match: match;
  merchant: Merchant;
};

type TransactionSummary = {
  avgAov: number;
  totalTransactionAmount: number;
  numberOfTransactions: number;
};

type ResponseData = {
  cnTransactionsByMarketplace: {
    [key: string]: Transactions;
  };
  transactionSummary: {
    result: TransactionSummary[];
  };
  loading?: boolean;
  error?: ApolloError;
};

const DEFAULT = {
  totalTransactionAmount: 0,
  avgAov: 0,
  numberOfTransactions: 0,
};

const renderString = ({ value }: any) => (
  <span>${abbreviateNumber(value, 2)}</span>
);

const total = (list: any) => (list ? sum(pluck('count' as any, list)) : 0);

const renderStats = (
  classes: Styles,
  label: string,
  value: string | number = 0,
  Component?: any,
  options?: Record<string, unknown>,
) => (
  <Statistic>
    <Statistic.Label className={classes.label}>{label}</Statistic.Label>
    <Statistic.Value>
      <Component value={value} {...options} />
    </Statistic.Value>
  </Statistic>
);

const renderTransactionCount = (
  classes: Styles,
  hasCampaign: boolean | null,
) => (response: ResponseData) => {
  const { totalTransactionAmount, avgAov, numberOfTransactions } = pathOr(
    DEFAULT,
    ['data', 'transactionSummary', 'result', 0],
    response,
  );

  const { AMEX, VISA, MASTERCARD } = pathOr(
    [],
    ['data', 'cnTransactionsByMarketplace', 'DOSH'],
    response,
  );

  if (response.loading) {
    return <MerchantStatsPlaceholder />;
  }

  return (
    <Grid className={classes.container}>
      <Grid.Row>
        {!hasCampaign && <p>Merchant&apos;s campaign has not started yet</p>}
      </Grid.Row>
      <Grid.Row>
        {renderStats(
          classes,
          'Total Sales',
          totalTransactionAmount,
          renderString,
        )}
        {renderStats(classes, 'AOV', avgAov, FormattedNumber, {
          style: 'currency',
          currency: 'USD',
          currencyDisplay: 'symbol',
        })}
        {renderStats(classes, 'VISA', total(VISA), FormattedNumber, {
          style: 'decimal',
          maximumFractionDigits: 1,
        })}
        {renderStats(
          classes,
          'MASTERCARD',
          total(MASTERCARD),
          FormattedNumber,
          {
            style: 'decimal',
            maximumFractionDigits: 1,
          },
        )}
        {renderStats(classes, 'AMEX', total(AMEX), FormattedNumber, {
          style: 'decimal',
          maximumFractionDigits: 1,
        })}
        {renderStats(
          classes,
          'Total Transactions',
          numberOfTransactions,
          FormattedNumber,
          {
            style: 'decimal',
            maximumFractionDigits: 1,
          },
        )}
      </Grid.Row>
    </Grid>
  );
};

const MerchantCNStatusPanel: FunctionComponent<Props> = ({
  merchantId,
  match,
  merchant,
  classes,
}) => {
  const hasCampaign = merchant.firstLiveDate !== null;
  const endDate = moment(new Date()).format('YYYY-MM-DD');
  const startDate = hasCampaign
    ? moment(merchant.firstLiveDate).format('YYYY-MM-DD')
    : endDate;

  return (
    <Query
      enableProgressInfo
      query={GET_MERCHANT_CN_STATUS}
      variables={{
        cnInput: {
          startDate,
          endDate,
          merchantId,
        },
        reportInput: {
          startDate,
          endDate,
          merchantName: path(['params', 'merchantName'], match),
          timeslice: 'ALL_TIME',
        },
      }}
      render={renderTransactionCount(classes, hasCampaign)}
    />
  );
};

export default withStyles(styles)(MerchantCNStatusPanel);
